import React, { useState, useEffect } from "react";
import {
  Box,
  Card as MuiCard,
  CardContent,
  FormGroup,
  InputAdornment,
  Typography,
  useTheme,
  CircularProgress
} from "@mui/material";
import SettingsCardHeader, {
  getSettingsCardShellSx,
} from "../../../components/Setting/SettingsCardHeader.jsx";
import "./style.css";
import { Card, CardBody, CardHeader, CardTitle, Col } from "reactstrap";
import { handleAuth } from "../../../components/Login/Auth.jsx";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSwal from "../../../components/Swal/CustomSwal.jsx";
import InputText from "../../../components/InputFields/InputText.jsx";
import AxiosInstance from "../../AxiosInstance.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InputDropdown from "../../../components/InputFields/InputDropdown.jsx";
import {
  DeleteIcone,
  EditIcon,
  LoaderComponent,
} from "../../../components/Icon/Index.jsx";
import BlueButton from "../../../components/Button/BlueButton.jsx";
import WhiteButton from "../../../components/Button/WhiteButton.jsx";
import { Grid } from "@mui/material";
import showToast from "../../../components/Toast/Toster.jsx";
import { useUserType } from "../../../contexts/UserTypeContext";
import { useMediaQuery } from "@mui/material";

/** Action tiles — same as quotes / superadmin tables */
const MAIL_EDIT_ACTION_BG = "#DCFCE7";
const MAIL_EDIT_ICON_COLOR = "#047857";
const MAIL_DELETE_ACTION_BG = "#FEE2E2";
const MAIL_DELETE_ICON_COLOR = "#B91C1C";

const MailService = () => {
  const theme = useTheme();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  // Define colors based on donation mode
  const colors = {
    bgClass: isDonationMode ? "bg-donor-color" : "bg-blue-color",
    borderClass: isDonationMode ? "border-donor-color" : "border-blue-color",
    textClass: isDonationMode ? "text-donor-color" : "text-blue-color",
    borderColor: isDonationMode ? "#29B3E2" : "#0f2b46",
    undoButtonBg: isDonationMode ? "#66D59A" : "var(--amber)",
  };

  const isDarkMail = theme.palette.mode === "dark";
  const mailHeaderBg = isDarkMail ? "#1a2332" : "#F9FAFB";
  const mailBodyBg = isDarkMail ? "#151d2a" : "#ffffff";
  const mailTitleColor = isDarkMail ? "#e8eaed" : "#1F2937";
  const mailFieldLabelColor = isDarkMail ? "#b8c5d6" : "#1f2937";
  const mailValueColor = isDarkMail ? "#e8eaed" : "#1f2937";
  const mailBorder = isDarkMail ? "#2d3b4e" : "#E5E7EB";

  const navigate = useNavigate();
  const location = useLocation();
  const [toMail, setToMail] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTokenData = async () => {
      try {
        const data = await handleAuth(navigate, location);
        if (data.data) {
          setToMail(data?.data?.EmailAddress);
        }
      } catch (error) {
        console.error("Error: ", error.message);
      }
    };
    fetchTokenData();
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittings, setIsSubmittings] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [open, setOpen] = useState(false);
  const [isDeleteSwalOpen, setIsDeleteSwalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const handleClickOpen = () => {
    setSubmitAttempted(false);
    setOpen(true);
  };
  const handleClose = () => {
    mailFormik.resetForm();
    setSubmitAttempted(false);
    setOpen(false);
  };

  const mailFormik = useFormik({
    initialValues: {
      Secure: false,
      ToMail: "",
      Host: "",
      Port: "",
      User: "",
      Password: "",
      Mail: "",
      Verify: false,
      IsDefault: false,
    },
    validationSchema: Yup.object({
      Host: Yup.string().required("Host required."),
      Port: Yup.string().required("Port required."),
      User: Yup.string().required("User required."),
      Password: Yup.string().required("Pass required."),
      Mail: Yup.string().required("Mail required."),
      Verify: Yup.boolean()
        .oneOf([true], "Mail service is not verified.")
        .required("Mail service is not verified."),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, setFieldTouched, validateForm, setErrors }) => {
      setSubmitAttempted(true);
      
      setFieldTouched("Host", true);
      setFieldTouched("Port", true);
      setFieldTouched("User", true);
      setFieldTouched("Password", true);
      setFieldTouched("Mail", true);
      setFieldTouched("Verify", true);
      
      await new Promise(resolve => setTimeout(resolve, 0));
      
      const validationErrors = await validateForm();
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setSubmitting(false);
        setIsSubmittings(false);
        return;
      }
      
      // Continue with original submission logic
      try {
        setIsSubmittings(true);

        // Remove fields we don't want to send to the API
        const { id, Verify, ToMail, ...dataToSend } = values;

        const requestData = {
          ...dataToSend,
          Secure: values.Secure === "true",
        };

        if (values.id === "default") {
          // Save default configuration
          const response = await AxiosInstance.post(`/v1/mail-configuration/`, {
            ...requestData,
            IsDefault: true,
          });

          if (response?.data?.statusCode === 200) {
            setSubmitAttempted(false);
            handleClose();
            fetchMailConfigurations();
            setTimeout(() => {
              showToast.success("Default configuration updated successfully");
            }, 500);
          } else {
            setTimeout(() => {
              showToast.error(
                response?.data?.message ||
                  "Failed to update default configuration"
              );
            }, 500);
          }
        } else if (values.id) {
          // Update existing configuration
          const response = await AxiosInstance.put(
            `/v1/mail-configuration/${values.id}`,
            requestData
          );

          if (response?.data?.statusCode === 200) {
            setSubmitAttempted(false);
            handleClose();
            fetchMailConfigurations();
            setTimeout(() => {
              showToast.success(response?.data?.message);
            }, 500);
          } else {
            setTimeout(() => {
              showToast.error(response?.data?.message);
            }, 500);
          }
        } else {
          // Create new configuration
          const response = await AxiosInstance.post(`/v1/mail-configuration`, {
            ...requestData,
            IsDefault: false,
          });

          if (response?.data?.statusCode === 200) {
            setSubmitAttempted(false);
            setTimeout(() => {
              showToast.success(response?.data?.message);
            }, 500);
            handleClose();
            fetchMailConfigurations();
          } else {
            setTimeout(() => {
              showToast.error(response?.data?.message);
            }, 500);
          }
          setIsSubmittings(false);
        }
      } catch (error) {
        console.error("Error: ", error.message);
        setTimeout(() => {
          showToast.error("An error occurred while processing your request.");
        }, 500);
        setIsSubmittings(false);
      } finally {
        setIsSubmittings(false);
      }
    },
  });
  const [showPassword, setShowPassword] = useState(false);

  // Helper function to reset Verify when config fields change
  const handleConfigFieldChange = (fieldName, value, isSecure = false) => {
    // Reset Verify to false when any configuration field changes
    if (mailFormik.values.Verify === true) {
      mailFormik.setFieldValue("Verify", false);
    }
    
    if (isSecure) {
      mailFormik.setFieldValue(fieldName, value);
    } else {
      mailFormik.handleChange({
        target: {
          name: fieldName,
          value: value,
        },
      });
    }
  };

  // Match Plan.jsx: show summary whenever any visible field shows a red border
  // (submit attempted OR field touched), not only after submitAttempted alone.
  // Verify is excluded — its error stays near the Verify Email button.
  const touched = mailFormik?.touched;
  const errors = mailFormik?.errors;
  const hasTouchedErrors = Boolean(
    (errors?.Host && (submitAttempted || touched?.Host)) ||
      (errors?.Port && (submitAttempted || touched?.Port)) ||
      (errors?.User && (submitAttempted || touched?.User)) ||
      (errors?.Password && (submitAttempted || touched?.Password)) ||
      (errors?.Mail && (submitAttempted || touched?.Mail)) ||
      (errors?.Secure && (submitAttempted || touched?.Secure))
  );
  const shouldShowError = hasTouchedErrors;

  const verifyEmail = async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields before testing
      if (
        !mailFormik.values.Host ||
        !mailFormik.values.Port ||
        !mailFormik.values.User ||
        !mailFormik.values.Password ||
        !mailFormik.values.Mail
      ) {
        setTimeout(() => {
          showToast.error("Please fill in all required fields before verifying");
        }, 500);
        setIsSubmitting(false);
        return;
      }

      mailFormik.values["ToMail"] = toMail;
      const response = await AxiosInstance.post(
        `/v1/mail-configuration/test_mail`,
        {
          Host: mailFormik.values.Host,
          Port: mailFormik.values.Port,
          User: mailFormik.values.User,
          Password: mailFormik.values.Password,
          Mail: mailFormik.values.Mail,
          Secure: mailFormik.values.Secure === "true",
          ToMail: toMail,
        }
      );

      if (response?.data?.statusCode === 200) {
        if (response?.data?.info?.verified) {
          mailFormik.setFieldValue("Verify", true);
          setTimeout(() => {
            showToast.success("Mail configuration verified successfully");
          }, 500);
        } else {
          // Display user-friendly error message from backend
          const errorMessage = response?.data?.info?.message || 
                              response?.data?.message ||
                              "Verification failed. Please check your mail settings.";
          setTimeout(() => {
            showToast.error(errorMessage);
          }, 500);
        }
      } else {
        // Handle non-200 status codes
        const errorMessage = response?.data?.info?.message ||
                            response?.data?.message ||
                            "We couldn't verify those SMTP credentials. Please check them and try again.";
        setTimeout(() => {
          showToast.error(errorMessage);
        }, 500);
      }
    } catch (error) {
      console.error("Error: ", error);
      // Handle network errors and axios errors
      const errorMessage = error?.response?.data?.info?.message ||
                          error?.response?.data?.message ||
                          "Connection failed. Please check your mail server settings.";
      setTimeout(() => {
        showToast.error(errorMessage);
      }, 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [mailConfigs, setMailConfigs] = useState([]);
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [deletedItemIds, setDeletedItemIds] = useState(() => {
    const stored = localStorage.getItem("deletedMailConfigs");
    return stored ? JSON.parse(stored) : [];
  });

  const fetchMailConfigurations = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get("/v1/mail-configuration");

      if (response?.data?.statusCode === 200) {
        const allConfigs = response?.data?.data || [];

        // Find the default configuration
        const defaultConfig = allConfigs.find(
          (config) => config.IsDefault === true
        );
        if (defaultConfig) {
          setDefaultConfig(defaultConfig);
        }

        // Filter out the default config and deleted items from the regular list
        const filteredData = allConfigs.filter(
          (item) =>
            !deletedItemIds.includes(item.MailConfigurationId) &&
            !item.IsDefault
        );

        setMailConfigs(filteredData);
      } else {
        console.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMailConfigurations();
  }, [deletedItemIds]);

  useEffect(() => {
    return () => {};
  }, []);
  const handleEditConfig = async (id) => {
    try {
      if (id === "default") {
        try {
          // Fetch the default configuration from the API
          const response = await AxiosInstance.get("/v1/mail-configuration");
          if (response?.data?.statusCode === 200) {
            const defaultConfig = response?.data?.data?.find(
              (config) => config.IsDefault === true
            );
            if (defaultConfig) {
              // If default config exists, use its values
              mailFormik.setValues({
                Host: defaultConfig.Host,
                Port: defaultConfig.Port,
                User: defaultConfig.User,
                Password: defaultConfig.Password || "",
                Mail: defaultConfig.Mail,
                Secure: defaultConfig.Secure?.toString(),
                Verify: defaultConfig.Verify || false,
                IsDefault: true,
                id: "default",
              });
            } else {
              // If no default config exists, set empty values
              mailFormik.setValues({
                Host: "",
                Port: "",
                User: "",
                Password: "",
                Mail: "",
                Secure: "false",
                Verify: false,
                IsDefault: true,
                id: "default",
              });
            }
          }
        } catch (error) {
          console.error("Error fetching mail configurations:", error);
          // Set empty values in case of error
          mailFormik.setValues({
            Host: "",
            Port: "",
            User: "",
            Password: "",
            Mail: "",
            Secure: "false",
            Verify: false,
            IsDefault: true,
            id: "default",
          });
        }
        // Use requestAnimationFrame to ensure form values are set before modal opens
        requestAnimationFrame(() => {
          setOpen(true);
        });
      } else {
        const response = await AxiosInstance.get(
          `/v1/mail-configuration/${id}`
        );
        if (response?.data?.statusCode === 200) {
          const config = response?.data?.result;
          mailFormik.setValues({
            Host: config?.Host,
            Port: config?.Port,
            User: config?.User,
            Password: config?.Password,
            Mail: config?.Mail,
            Secure: config?.Secure?.toString(),
            Verify: config?.Verify,
            IsDefault: false,
            id: config?.MailConfigurationId,
          });
          // Use requestAnimationFrame to ensure form values are set before modal opens
          requestAnimationFrame(() => {
            setOpen(true);
          });
        } else {
          console.error("Failed to fetch mail configuration details.");
        }
      }
    } catch (error) {
      console.error("Error fetching mail configuration details:", error);
    }
  };
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = (id) => {
    if (!id) {
      showToast.warning("Cannot delete: No configuration selected");
      return;
    }
    // Prevent deletion of default configuration
    if (id === defaultConfig?.MailConfigurationId) {
      showToast.warning("Cannot delete default configuration");
      return;
    }
    setDeleteItemId(id);
    setIsDeleteSwalOpen(true);
  };

  const handleDeleteConfirm = async (reason) => {
    try {
      setIsDeleting(true);

      let apiResponse;

      // Add timeout to prevent hanging
      const deletePromise = AxiosInstance.delete(
        `/v1/mail-configuration/${deleteItemId}`,
        {
          isDelete: true,
        }
      );

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 10000)
      );

      apiResponse = await Promise.race([deletePromise, timeoutPromise]);
  
      if (apiResponse?.data?.statusCode === 400) {
        showToast.warning(
          apiResponse?.data?.message || "Cannot delete this configuration"
        );
        setIsDeleting(false);
        setIsDeleteSwalOpen(false);
        setDeleteItemId(null);
        return;
      }

      if (apiResponse?.data?.statusCode === 200) {
        const newDeletedIds = [...deletedItemIds, deleteItemId];
        setDeletedItemIds(newDeletedIds);

        localStorage.setItem(
          "deletedMailConfigs",
          JSON.stringify(newDeletedIds)
        );

        if (deleteItemId === defaultConfig?.MailConfigurationId) {
          setDefaultConfig(null);
        } else {
          setMailConfigs((prevData) =>
            prevData.filter(
              (item) => item?.MailConfigurationId !== deleteItemId
            )
          );
        }

        showToast.success(
          deleteItemId === defaultConfig?.MailConfigurationId
            ? "Default configuration has been unset successfully"
            : "Configuration deleted successfully"
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Delete failed! Please try again."
      );
    } finally {
      setIsDeleting(false);
      setDeleteItemId(null);
      setIsDeleteSwalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteSwalOpen(false);
    setDeleteItemId(null);
    setIsDeleting(false);
  };

  return (
    <Grid className="mail">
      <Grid className="justify-content-center align-items-center mb-3 ">
        <Grid className="d-flex justify-content-between mb-2 align-items-center"></Grid>

        <MuiCard
          elevation={0}
          sx={{
            ...getSettingsCardShellSx(theme),
            marginBottom: "20px",
            height: "100%",
          }}
        >
          <SettingsCardHeader title="Mail Service" plainHeader />
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Grid
              style={{
                paddingLeft: "40px",
                paddingRight: "40px",
                paddingTop: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                className={colors.textClass}
                style={{
                  fontWeight: 600,
                  fontSize: "18px",
                  margin: 0,
                }}
              >
                Set your SMTP mail configuration settings from here
              </Typography>
              <Grid className="mailBtnSuperadmin">
                <BlueButton
                  onClick={handleClickOpen}
                  label={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <AddIcon fontSize="small" aria-hidden />
                      Add new configuration
                    </span>
                  }
                  className={`text-uppercase ${colors.bgClass} text-white-color`}
                  style={{
                    padding: "5px",
                    border: `2px solid ${colors.borderColor}`,
                    borderRadius: "5px",
                  }}
                  isDonationMode={isDonationMode}
                >
                  Add new configuration
                </BlueButton>
              </Grid>
            </Grid>
            <Grid>
              {loading ? (
                <Grid className="d-flex flex-direction-row justify-content-center align-items-center Typography-5 m-5">
                  <LoaderComponent loader={loading} height="50" width="50" isDonationMode={isDonationMode} />
                </Grid>
              ) : (
                <Grid
                  className="configuration-box"
                  style={{
                    paddingLeft: "40px",
                    paddingTop: "20px",
                    paddingRight: "40px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    minHeight: "200px",
                    alignItems: "center",
                    justifyContent:
                      mailConfigs?.length === 0 ? "center" : "flex-start",
                  }}
                >
                  <Card
                    className="my-2 mail-card"
                    style={{
                      flex: "0 0 calc(50% - 10px)",
                      border: `1px solid ${mailBorder}`,
                      borderRadius: "12px",
                      boxShadow: "var(--shadow-md)",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: isDarkMail ? "#1a2332" : undefined,
                    }}
                  >
                    <CardHeader
                      className="d-flex justify-content-between align-items-center mail-header config-head"
                      style={{
                        backgroundColor: mailHeaderBg,
                        borderBottom: `1px solid ${mailBorder}`,
                        padding: "14px 18px",
                      }}
                    >
                      <Typography
                        className="mb-0"
                        style={{ fontSize: "17px", fontWeight: 600, color: mailTitleColor }}
                      >
                        Default Configuration
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          backgroundColor: MAIL_EDIT_ACTION_BG,
                          border: "1px solid rgba(4, 120, 87, 0.18)",
                          boxShadow: "var(--shadow-sm)",
                          cursor: "pointer",
                        }}
                        onClick={() => handleEditConfig("default")}
                      >
                        <EditIcon
                          color={MAIL_EDIT_ICON_COLOR}
                          style={{ marginRight: 0, fontSize: "18px" }}
                        />
                      </Box>
                    </CardHeader>
                    <CardBody
                      style={{
                        maxHeight: "200px",
                        overflowX: "auto",
                        overflowY: "clip",
                        background: mailBodyBg,
                        padding: "16px 18px",
                        borderTop: `1px solid ${mailBorder}`,
                      }}
                    >
                      <Grid style={{}}>
                        <Grid style={{ minWidth: "300px" }}>
                          <Grid className="host-mail-port">
                            <CardTitle
                              tag="Typography"
                              className={colors.textClass}
                              style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                            >
                              Host :
                            </CardTitle>
                            <Typography
                              className={`mb-3 ${colors.textClass} mail-section`}
                              style={{
                                opacity: isDarkMail ? 1 : 0.5,
                                fontWeight: 500,
                                color: mailValueColor,
                              }}
                            >
                              {defaultConfig?.Host || "Not set"}
                            </Typography>
                          </Grid>
                          <Grid className="host-mail-port">
                            <CardTitle
                              tag="Typography"
                              className={colors.textClass}
                              style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                            >
                              Port :
                            </CardTitle>
                            <Typography
                              className={colors.textClass}
                              style={{
                                opacity: isDarkMail ? 1 : 0.5,
                                fontWeight: 500,
                                color: mailValueColor,
                              }}
                            >
                              {defaultConfig?.Port || "Not set"}
                            </Typography>
                          </Grid>
                          <Grid className="host-mail-port">
                            <CardTitle
                              tag="Typography"
                              className={colors.textClass}
                              style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                            >
                              User :
                            </CardTitle>
                            <Typography
                              className={`mb-3 ${colors.textClass}`}
                              style={{
                                opacity: isDarkMail ? 1 : 0.5,
                                fontWeight: 500,
                                color: mailValueColor,
                              }}
                            >
                              {defaultConfig?.User || "Not set"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardBody>
                  </Card>

                  {mailConfigs && mailConfigs.length > 0 ? (
                    mailConfigs.map((config, index) => (
                      <Card
                        className="my-2 mail-card"
                        style={{
                          flex: "0 0 calc(50% - 10px)",
                          border: `1px solid ${mailBorder}`,
                          borderRadius: "12px",
                          boxShadow: "var(--shadow-md)",
                          overflow: "hidden",
                          backgroundColor: isDarkMail ? "#1a2332" : undefined,
                        }}
                        key={config?.MailConfigurationId ?? index}
                      >
                        <CardHeader
                          className="d-flex justify-content-between align-items-center mail-header config-head"
                          style={{
                            backgroundColor: mailHeaderBg,
                            borderBottom: `1px solid ${mailBorder}`,
                            padding: "14px 18px",
                          }}
                        >
                          <Typography
                            className="mb-0"
                            style={{ fontSize: "17px", fontWeight: 600, color: mailTitleColor }}
                          >
                            Configuration Details
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditConfig(config?.MailConfigurationId);
                              }}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 36,
                                height: 36,
                                borderRadius: "8px",
                                backgroundColor: MAIL_EDIT_ACTION_BG,
                                border: "1px solid rgba(4, 120, 87, 0.18)",
                                boxShadow: "var(--shadow-sm)",
                                cursor: "pointer",
                              }}
                            >
                              <EditIcon
                                color={MAIL_EDIT_ICON_COLOR}
                                style={{ marginRight: 0 }}
                              />
                            </Box>
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(config?.MailConfigurationId);
                              }}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 36,
                                height: 36,
                                borderRadius: "8px",
                                backgroundColor: MAIL_DELETE_ACTION_BG,
                                border: "1px solid rgba(185, 28, 28, 0.15)",
                                boxShadow: "var(--shadow-sm)",
                                cursor: "pointer",
                              }}
                            >
                              <DeleteIcone
                                className="customerEditImgToEdit"
                                color={MAIL_DELETE_ICON_COLOR}
                                style={{ marginRight: 0 }}
                              />
                            </Box>
                          </Box>
                        </CardHeader>
                        <CardBody
                          style={{
                            maxHeight: "200px",
                            overflowX: "auto",
                            overflowY: "clip",
                            background: mailBodyBg,
                            padding: "16px 18px",
                            borderTop: `1px solid ${mailBorder}`,
                          }}
                        >
                          <Grid style={{}}>
                            <Grid style={{ minWidth: "300px" }}>
                              <Grid className="host-mail-port">
                                <CardTitle
                                  tag="Typography"
                                  className={colors.textClass}
                                  style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                                >
                                  Host :
                                </CardTitle>
                                <Typography
                                  className={`mb-3 ${colors.textClass} mail-section`}
                                  style={{
                                    opacity: isDarkMail ? 1 : 0.5,
                                    fontWeight: 500,
                                    color: mailValueColor,
                                  }}
                                >
                                  {config?.Host || "Host not available"}
                                </Typography>
                              </Grid>
                              <Grid className="host-mail-port">
                                <CardTitle
                                  tag="Typography"
                                  className={colors.textClass}
                                  style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                                >
                                  Port :
                                </CardTitle>
                                <Typography
                                  className={colors.textClass}
                                  style={{
                                    opacity: isDarkMail ? 1 : 0.5,
                                    fontWeight: 500,
                                    color: mailValueColor,
                                  }}
                                >
                                  {config?.Port}
                                </Typography>
                              </Grid>
                              <Grid className="host-mail-port">
                                <CardTitle
                                  tag="Typography"
                                  className={colors.textClass}
                                  style={{ fontWeight: 500, fontSize: "14px", color: mailFieldLabelColor }}
                                >
                                  User :
                                </CardTitle>
                                <Typography
                                  className={`mb-3 ${colors.textClass}`}
                                  style={{
                                    opacity: isDarkMail ? 1 : 0.5,
                                    fontWeight: 500,
                                    color: mailValueColor,
                                  }}
                                >
                                  {config?.User}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </CardBody>
                      </Card>
                    ))
                  ) : (
                    <Grid
                      style={{
                        width: "100%",
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#888",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.8,
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/4076/4076549.png"
                        alt="No Data"
                        style={{ width: 80, marginBottom: 16, opacity: 0.7 }}
                      />
                      <Typography
                        style={{
                          fontSize: "20px",
                          fontWeight: 600,
                          color: "#0f2b46",
                          marginBottom: 8,
                        }}
                      >
                        No Mail Configurations Found
                      </Typography>
                      <Typography style={{ fontSize: "15px", color: "#888" }}>
                        You have not added any mail configuration yet.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </CardContent>
        </MuiCard>
      </Grid>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        className="new_confi"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{ m: 0, p: 2, fontWeight: 600, fontSize: "18px" }}
          className={`${colors.textClass} borerBommoModel`}
          id="customized-dialog-title"
        >
          {mailFormik.values.id === "default" 
            ? "Default Mail Configuration" 
            : "Mail Configuration"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            setSubmitAttempted(false);
            handleClose();
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: isDonationMode
              ? "var(--bg-donor-color)"
              : "var(--text-blue-color)",
            "& .MuiSvgIcon-root": { color: "inherit" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ padding: { xs: "16px 20px", sm: "20px 24px" } }}>
          <CardTitle 
            tag="h6" 
            className={colors.textClass}
            style={{ 
              marginBottom: "24px",
              fontSize: "16px",
              fontWeight: 500
            }}
          >
              {mailFormik.values.id === "default" 
              ? "Configure default SMTP mail settings that will be used by all companies"
              : "Configure SMTP mail settings for this configuration"}
          </CardTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mailFormik?.handleSubmit();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.type !== "submit") {
                e.preventDefault();
              }
            }}
          >
          <Grid container spacing={3} className="mail-modal-main">
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormGroup className="mail-form-group">
                <InputText
                  name="Host"
                  label="Host *"
                  placeholder="Enter SMTP host"
                  fieldHeight="54px"
                  size={isMobile && "small"}
                  id="Host"
                  type="text"
                  className={`${colors.textClass} w-100`}
                  value={mailFormik?.values?.Host}
                  onChange={(e) => {
                    handleConfigFieldChange("Host", e.target.value);
                  }}
                  onBlur={mailFormik?.handleBlur}
                  error={(submitAttempted && Boolean(mailFormik?.errors?.Host)) ||
                         (mailFormik?.touched?.Host && Boolean(mailFormik?.errors?.Host))}
                  helperText={undefined}
                  showErrorOnly={true}
                />
              </FormGroup>
              <FormGroup className="mail-form-group">
                <InputText
                  name="User"
                  label="User *"
                  placeholder="Enter username or email"
                  size={isMobile && "small"}
                  fieldHeight="54px"
                  id="User"
                  type="text"
                  className={`${colors.textClass} w-100`}
                  value={mailFormik?.values?.User}
                  onChange={(e) => {
                    handleConfigFieldChange("User", e.target.value);
                  }}
                  onBlur={mailFormik?.handleBlur}
                  error={(submitAttempted && Boolean(mailFormik?.errors?.User)) ||
                         (mailFormik?.touched?.User && Boolean(mailFormik?.errors?.User))}
                  helperText={undefined}
                  showErrorOnly={true}
                />
              </FormGroup>
              <FormGroup className="mail-form-group">
                <InputDropdown
                  options={["true", "false"]}
                  value={mailFormik?.values?.Secure || null}
                  inputValue={mailFormik?.values?.Secure || null}
                  onChange={(e, value) => {
                    handleConfigFieldChange("Secure", value, true);
                  }}
                  onBlur={mailFormik?.handleBlur}
                  label="Secure"
                  name="Secure"
                  placeholder="Select secure connection"
                  type="text"
                  getOptionLabel={(option) => option || ""}
                  filterOptions={(options, state) => {
                    return options.filter((option) =>
                      option
                        ?.toLowerCase()
                        ?.includes(state?.inputValue?.toLowerCase())
                    );
                  }}
                  error={(submitAttempted && Boolean(mailFormik?.errors?.Secure)) ||
                         (mailFormik?.touched?.Secure && Boolean(mailFormik?.errors?.Secure))}
                  helperText={undefined}
                  showErrorOnly={true}
                  textFieldProps={{
                    fieldHeight: "54px",
                    className: `${colors.textClass} w-100 mail-dropdown-input`,
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormGroup className="mail-form-group">
                <InputText
                  name="Port"
                  label="Port *"
                  placeholder="Enter SMTP port"
                  size={isMobile && "small"}
                  fieldHeight="54px"
                  id="Port"
                  type="text"
                  className={`${colors.textClass} w-100`}
                  value={mailFormik?.values?.Port}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      handleConfigFieldChange("Port", Math.max(0, Number(value)));
                    }
                  }}
                  onBlur={mailFormik?.handleBlur}
                  error={(submitAttempted && Boolean(mailFormik?.errors?.Port)) ||
                         (mailFormik?.touched?.Port && Boolean(mailFormik?.errors?.Port))}
                  helperText={undefined}
                  showErrorOnly={true}
                />
              </FormGroup>
              <FormGroup className="mail-form-group">
                <InputText
                  value={mailFormik?.values?.Password}
                  placeholder="Enter SMTP password"
                  size={isMobile && "small"}
                  onChange={(e) => {
                    handleConfigFieldChange("Password", e.target.value);
                  }}
                  onBlur={mailFormik?.handleBlur}
                  autoComplete="new-password"
                  error={(submitAttempted && Boolean(mailFormik?.errors?.Password)) ||
                         (mailFormik?.touched?.Password && Boolean(mailFormik?.errors?.Password))}
                  helperText={undefined}
                  showErrorOnly={true}
                  name="Password"
                  label="Password *"
                  type={showPassword ? "text" : "password"}
                  className={`${colors.textClass} w-100`}
                  fieldHeight="54px"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormGroup>
              <FormGroup className="mail-form-group">
                <InputText
                  name="Mail"
                  label="Mail *"
                  placeholder="Enter sender email address"
                  size={isMobile && "small"}
                  fieldHeight="54px"
                  id="Mail"
                  type="text"
                  className={`${colors.textClass} w-100`}
                  value={mailFormik?.values?.Mail}
                  onChange={(e) => {
                    handleConfigFieldChange("Mail", e.target.value);
                  }}
                  onBlur={mailFormik?.handleBlur}
                  error={(submitAttempted && Boolean(mailFormik?.errors?.Mail)) ||
                         (mailFormik?.touched?.Mail && Boolean(mailFormik?.errors?.Mail))}
                  helperText={undefined}
                  showErrorOnly={true}
                />
              </FormGroup>
            </Grid>
          </Grid>
          
          {shouldShowError && (
            <Grid className="mt-2 mb-2">
              <Typography
                sx={{
                  color: "#dc3545",
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Please correct the red boxes above
              </Typography>
            </Grid>
          )}
          </form>
        </DialogContent>
        <DialogActions
          sx={{
            margin: 0,
            padding: { xs: "16px 20px", sm: "20px 24px" },
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            display: "block",
            minHeight: "60px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "flex-start",
              gap: 2,
            }}
          >
            {/* Left: Cancel Button - same vertical structure as right (spacer + row) so buttons align */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                alignItems: isMobile ? "stretch" : "flex-start",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {!isMobile && <Box sx={{ minHeight: "16px" }} aria-hidden />}
              <WhiteButton
                onClick={() => {
                  setSubmitAttempted(false);
                  handleClose();
                }}
                type="button"
                label="Cancel"
                style={{ width: isMobile ? "100%" : undefined, minWidth: isMobile ? undefined : 100 }}
              />
            </Box>

            {/* Right: Error Message + Verify Email + Add Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "stretch" : "flex-end",
                gap: 0.5,
                width: isMobile ? "100%" : "auto",
                minWidth: 0,
              }}
            >
              {/* Error Message */}
              <Box
                sx={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  minHeight: "16px",
                  lineHeight: "16px",
                  visibility: submitAttempted && mailFormik?.errors?.Verify ? "visible" : "hidden",
                }}
              >
                {mailFormik?.errors?.Verify || "\u00A0"}
              </Box>

              {/* Verify Email + Add Email - row on desktop, full-width stack on mobile */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: isMobile ? "stretch" : "center",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {/* JOB-3347: keep buttons visible during request; spinner inside submit. */}
                <BlueButton
                  onClick={verifyEmail}
                  disabled={isSubmitting}
                  label={
                    isSubmitting ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress
                          size={16}
                          thickness={5}
                          sx={{ color: "#fff" }}
                        />
                        <span>Verifying…</span>
                      </Box>
                    ) : (
                      "Verify Email"
                    )
                  }
                  className="email-blue-button configurationAddMail"
                  isDonationMode={isDonationMode}
                  style={{
                    minWidth: 120,
                    flexShrink: 0,
                    width: isMobile ? "100%" : undefined,
                    opacity: isSubmitting ? 0.85 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                />
                <BlueButton
                  onClick={mailFormik?.handleSubmit}
                  disabled={!mailFormik.values.Verify || isSubmittings}
                  label={
                    isSubmittings ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress
                          size={16}
                          thickness={5}
                          sx={{ color: "#fff" }}
                        />
                        <span>
                          {mailFormik.values.id ? "Updating…" : "Saving…"}
                        </span>
                      </Box>
                    ) : mailFormik.values.id ? (
                      "Update Email"
                    ) : (
                      "Add Email"
                    )
                  }
                  className="email-blue-button configurationAddMail"
                  isDonationMode={isDonationMode}
                  style={{
                    minWidth: 120,
                    flexShrink: 0,
                    width: isMobile ? "100%" : undefined,
                    opacity: !mailFormik.values.Verify || isSubmittings ? 0.85 : 1,
                    cursor:
                      !mailFormik.values.Verify || isSubmittings
                        ? "not-allowed"
                        : "pointer",
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <CustomSwal
        isOpen={isDeleteSwalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Are you sure?"
        text="Once deleted, you will not be able to recover!"
        confirmButtonText={isDeleting ? "Deleting..." : "Delete"}
        cancelButtonText="Cancel"
        placeholder="Enter reason for deletion"
        requireInput={true}
        disabled={isDeleting}
        isDonationMode={isDonationMode}
      />
    </Grid>
  );
};

export default MailService;
